import { CSSProperties, forwardRef } from 'react';

import { SizeProps } from '../../styles';
import { extractSizeProps } from '../../styles/size.styles';
import type { DefaultComponentProps } from '../../types';
import { cn, extractRest, prefixKeysStyled } from '../../utils';

import { RadioButtonClassNames } from './constants';
import getRadioButtonMode from './getRadioButtonMode';
import {
  RadioButtonInput,
  RadioButtonLabel,
  RadioButtonRoot,
} from './RadioButton.styled';
import RadioButtonFinishedOverlay from './RadioButtonFinishedOverlay';

export type RadioButtonProps = React.InputHTMLAttributes<HTMLInputElement> &
  DefaultComponentProps &
  SizeProps & {
    /**
     * @note
     * It's recommended to use `string` for most situation,
     * however if you need custom render of label then don't forget to also pass `aria-label`
     */
    label: string | React.ReactNode;
    isLabelHidden?: boolean;
    hasError?: boolean;
    whiteSpace?: CSSProperties['whiteSpace'];
    finished?: boolean;
  };

const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (props, forwardedRef) => {
    const sizeProps = extractSizeProps(props);
    const {
      'data-testid': dataTestId,
      isLabelHidden,
      label,
      className,
      disabled,
      hasError,
      whiteSpace,
      finished,
      checked,
      ...inputProps
    } = extractRest(props, sizeProps);

    const mode = getRadioButtonMode({ checked, finished });

    return (
      <RadioButtonRoot
        $isDisabled={disabled}
        $mode={mode}
        $whiteSpace={whiteSpace}
        className={cn(RadioButtonClassNames.Root, className)}
        data-testid={dataTestId}
        {...prefixKeysStyled(sizeProps)}>
        <RadioButtonInput
          aria-label={typeof label === 'string' ? label : undefined}
          {...inputProps}
          $hasError={hasError}
          $mode={mode}
          checked={checked}
          className={RadioButtonClassNames.Main}
          disabled={disabled}
          ref={forwardedRef}
          type="radio"
        />
        {!isLabelHidden && !!label && (
          <RadioButtonLabel className={RadioButtonClassNames.Label}>
            {label}
          </RadioButtonLabel>
        )}
        {mode === 'finished' ? (
          <RadioButtonFinishedOverlay isDisabled={disabled} />
        ) : null}
      </RadioButtonRoot>
    );
  },
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;
